import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, linkedSignal, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Client } from '../../../api/app-types/client.app';
import { Relative } from '../../../api/app-types/relative.app';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { Paginator } from '../../../core/components/paginator/paginator';
import { LvrSpacer } from '../../../core/components/spacer/spacer';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { emptyPaginatedResponse, PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ClientRelativesList } from './client-relatives-list/client-relatives-list';
import { OtherRelativesList } from './other-relatives-list/other-relatives-list';

interface ClientRelativePageSizeState {
	personal: number;
	other: number;
}

const clientRelativePageSizes = signal<Record<number, ClientRelativePageSizeState>>({});

@Component({
	selector: 'lvr-client-relatives',
	templateUrl: './client-relatives.html',
	imports: [
		TranslocoModule,
		LvrPaddingDirective,
		ClientRelativesList,
		OtherRelativesList,
		LvrSpacer,
		LvrHeading,
		LvrButton,
		Paginator,
		DatePipe,
	],
	styleUrl: './client-relatives.scss',
})
export class ClientRelatives extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly session = inject(SessionManagerService);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly data = toSignal(inject(ActivatedRoute).data);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly canCreateRelatives = hasPermission(this.session.permissions, 'ClientEnvironments.Create');
	public readonly page = computed<PaginatedResponse<Relative>>(
		() => this.data()?.['relatives'] ?? emptyPaginatedResponse<Relative>(),
	);

	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly client = computed<Client | undefined>(() => {
		const c = this.clientResolved();
		return c?.kind === 'full' ? c.value : undefined;
	});

	private readonly initialPage = computed<PaginatedResponse<Relative>>(() => {
		const pagination = this.page();
		return {
			entities: pagination.entities,
			offset: pagination.offset,
			range: pagination.range ?? this.env.pagination.range,
			totalEntities: pagination.totalEntities,
		};
	});

	public readonly linkedPage = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.pagination.range,
		}),
	});

	public readonly allPersonalRelatives = computed(() =>
		this.linkedPage().entities.filter((r) => r.categoryPersonCategory.isPersonal),
	);
	public readonly allOtherRelatives = computed(() =>
		this.linkedPage().entities.filter((r) => !r.categoryPersonCategory.isPersonal),
	);

	public readonly personalPageIndex = signal(0);
	public readonly personalPageSize = signal(this.env.defaultPageSize);
	public readonly otherPageIndex = signal(0);
	public readonly otherPageSize = signal(this.env.defaultPageSize);

	public readonly personalRelatives = computed(() => {
		const start = this.personalPageIndex() * this.personalPageSize();
		const end = start + this.personalPageSize();
		return this.allPersonalRelatives().slice(start, end);
	});

	public readonly otherRelatives = computed(() => {
		const start = this.otherPageIndex() * this.otherPageSize();
		const end = start + this.otherPageSize();
		return this.allOtherRelatives().slice(start, end);
	});

	public readonly showFirstLastButtons = true;
	public readonly hidePageSize = false;
	public readonly personalTotalItems = computed(() => this.allPersonalRelatives().length);
	public readonly otherTotalItems = computed(() => this.allOtherRelatives().length);
	private readonly lastRestoredClientId = signal<number | undefined>(undefined);

	private readonly restorePaginationOnClientChange = effect(() => {
		const currentClientId = this.client()?.id;
		if (currentClientId == null) return;
		if (this.lastRestoredClientId() === currentClientId) return;

		const savedPageSizes = untracked(() => clientRelativePageSizes()[currentClientId]);
		const savedPersonalFromStorage = this.getSavedPageSize('client-relatives-personal');
		const savedOtherFromStorage = this.getSavedPageSize('client-relatives-other');
		const defaultPageSize = this.env.defaultPageSize;
		untracked(() => {
			this.personalPageIndex.set(0);
			this.otherPageIndex.set(0);
			this.personalPageSize.set(savedPersonalFromStorage ?? savedPageSizes?.personal ?? defaultPageSize);
			this.otherPageSize.set(savedOtherFromStorage ?? savedPageSizes?.other ?? defaultPageSize);
			this.lastRestoredClientId.set(currentClientId);
		});
	});

	public openCreate() {
		if (!this.canCreateRelatives()) return;
		this.router.navigate(['add'], { relativeTo: this.route });
	}

	public handlePersonalPageEvent(event: PageEvent) {
		this.personalPageIndex.set(event.pageIndex);
		this.personalPageSize.set(event.pageSize);
		this.persistPageSizeForCurrentClient('personal', event.pageSize);
		this.savePageSize('client-relatives-personal', event.pageSize);
	}

	public handleOtherPageEvent(event: PageEvent) {
		this.otherPageIndex.set(event.pageIndex);
		this.otherPageSize.set(event.pageSize);
		this.persistPageSizeForCurrentClient('other', event.pageSize);
		this.savePageSize('client-relatives-other', event.pageSize);
	}

	private persistPageSizeForCurrentClient(type: keyof ClientRelativePageSizeState, value: number) {
		const currentClientId = this.client()?.id;
		if (currentClientId == null) return;

		const allPageSizes = clientRelativePageSizes();
		const currentState =
			allPageSizes[currentClientId] ??
			({
				personal: this.env.defaultPageSize,
				other: this.env.defaultPageSize,
			} as ClientRelativePageSizeState);

		clientRelativePageSizes.set({
			...allPageSizes,
			[currentClientId]: {
				...currentState,
				[type]: value,
			},
		});
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(preferenceKey: string): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, preferenceKey);
		if (!saved || !this.env.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}

	private savePageSize(preferenceKey: string, pageSize: number) {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return;
		this.storage.setPageSize(userId, preferenceKey, pageSize);
	}
}
